import { z } from "zod";
import { createMessagePollSchema } from "./extras";
import { encryptedPayloadSchema } from "./e2ee";

export const usernameSchema = z
  .string()
  .trim()
  .min(2, "At least 2 characters")
  .max(24, "At most 24 characters")
  .regex(/^[a-zA-Z0-9_]+$/, "Letters, numbers and underscores only");

export const passwordSchema = z.string().min(8, "At least 8 characters").max(128, "At most 128 characters");

export const registerSchema = z.object({
  username: usernameSchema,
  displayName: z.string().trim().min(1, "Required").max(50),
  email: z.string().trim().toLowerCase().email("Enter a valid email").max(254),
  password: passwordSchema,
  /** From the emailed invite link, while Atrium is invite-only. */
  inviteToken: z.string().min(20).max(200).optional(),
});

export const loginSchema = z.object({
  identifier: z.string().trim().min(1, "Required").max(254),
  password: z.string().min(1, "Required").max(128),
  /** The emailed code, when signing in from somewhere new. */
  code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code from the email").optional(),
});

export const NOTIFICATION_TYPES = [
  "follow",
  "like",
  "comment",
  "reply",
  "repost",
  "mention",
  "dm",
  "community_invite",
  "community_announcement",
  "friend_request",
  "friend_accept",
  "quote",
  "poll_ended",
  "reminder",
  "event",
  "badge",
  "report_update",
  "system",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const notificationPrefsSchema = z.object({
  follow: z.boolean(),
  like: z.boolean(),
  comment: z.boolean(),
  reply: z.boolean(),
  repost: z.boolean(),
  mention: z.boolean(),
  dm: z.boolean(),
  community_invite: z.boolean(),
  community_announcement: z.boolean(),
  friend_request: z.boolean().default(true),
  friend_accept: z.boolean().default(true),
  quote: z.boolean().default(true),
  poll_ended: z.boolean().default(true),
  reminder: z.boolean().default(true),
  event: z.boolean().default(true),
  badge: z.boolean().default(true),
  report_update: z.boolean().default(true),
  system: z.boolean().default(true),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  follow: true,
  like: true,
  comment: true,
  reply: true,
  repost: true,
  mention: true,
  dm: true,
  community_invite: true,
  community_announcement: true,
  friend_request: true,
  friend_accept: true,
  quote: true,
  poll_ended: true,
  reminder: true,
  event: true,
  badge: true,
  report_update: true,
  system: true,
};

const urlOrEmpty = z
  .string()
  .trim()
  .max(200)
  .refine((v) => v === "" || /^https?:\/\/[^\s]+$/i.test(v), "Must start with http:// or https://");

export const updateProfileSchema = z.object({
  displayName: z.string().trim().min(1).max(50).optional(),
  bio: z.string().trim().max(300).optional(),
  location: z.string().trim().max(60).optional(),
  website: urlOrEmpty.optional(),
  avatarMediaId: z.string().uuid().nullable().optional(),
  bannerMediaId: z.string().uuid().nullable().optional(),
});

export const dmPolicySchema = z.enum(["everyone", "following", "none"]);
export const updateSettingsSchema = z.object({
  username: usernameSchema.optional(),
  showCommunities: z.boolean().optional(),
  discoverable: z.boolean().optional(),
  readReceipts: z.boolean().optional(),
  dmPolicy: dmPolicySchema.optional(),
  invitePolicy: dmPolicySchema.optional(),
  showPresence: z.boolean().optional(),
  notificationPrefs: notificationPrefsSchema.partial().optional(),
  shareActivity: z.boolean().optional(),
  showLastSeen: z.boolean().optional(),
  friendPolicy: dmPolicySchema.optional(),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});

export const forgotPasswordSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email").max(254),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(10, "This link is incomplete").max(512),
  newPassword: passwordSchema,
});

/** A GIF picked from the provider, sent as an embed. */
export const gifAttachmentSchema = z.object({
  id: z.string().max(64),
  url: z.string().regex(/^https:\/\/[^\s]+$/).max(1024),
  previewUrl: z.string().regex(/^https:\/\/[^\s]+$/).max(1024),
  /** First frame, shown when animation is off. */
  stillUrl: z.string().regex(/^https:\/\/[^\s]+$/).max(1024).optional(),
  width: z.number().int().positive().max(4096),
  height: z.number().int().positive().max(4096),
  title: z.string().max(200).default(""),
});
export type GifAttachment = z.infer<typeof gifAttachmentSchema>;

export const POST_MAX = 1000;
export const POST_VISIBILITIES = ["public", "followers", "close_friends", "mentioned"] as const;
export type PostVisibility = (typeof POST_VISIBILITIES)[number];
export const REPLY_POLICIES = ["everyone", "following", "mentioned", "none"] as const;
export type ReplyPolicy = (typeof REPLY_POLICIES)[number];
export const POST_REACTIONS = ["like", "love", "haha", "wow", "sad", "fire"] as const;
export type PostReaction = (typeof POST_REACTIONS)[number];

export const pollInputSchema = z.object({
  options: z.array(z.string().trim().min(1).max(80)).min(2).max(6),
  multiple: z.boolean().default(false),
  durationHours: z.number().int().min(1).max(24 * 14).default(24),
});

const postFields = {
  content: z.string().max(POST_MAX).default(""),
  mediaIds: z.array(z.string().uuid()).max(10).default([]),
  quoteOfId: z.string().uuid().optional(),
  /** Publish on behalf of a community (requires PUBLISH_TO_FEED in a public community). */
  communityId: z.string().uuid().optional(),
  /** Share a message from a public community channel. */
  sharedMessageId: z.string().uuid().optional(),
  /** Continue a thread (your own post) or reply to someone's post as a post. */
  replyToPostId: z.string().uuid().optional(),
  visibility: z.enum(POST_VISIBILITIES).default("public"),
  replyPolicy: z.enum(REPLY_POLICIES).default("everyone"),
  contentWarning: z.string().trim().max(120).optional(),
  sensitive: z.boolean().default(false),
  location: z.string().trim().max(80).optional(),
  gif: gifAttachmentSchema.optional(),
  poll: pollInputSchema.optional(),
};

const postNotEmpty = (v: { content: string; mediaIds: string[]; quoteOfId?: string; sharedMessageId?: string; gif?: unknown; poll?: unknown }) =>
  v.content.trim().length > 0 || v.mediaIds.length > 0 || !!v.quoteOfId || !!v.sharedMessageId || !!v.gif || !!v.poll;

export const createPostSchema = z.object(postFields).refine(postNotEmpty, { message: "Post cannot be empty" });

/** Drafts may be empty-ish while being written; scheduling validates like publishing. */
export const postDraftSchema = z.object({
  ...postFields,
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
});

export const editPostSchema = z.object({
  content: z.string().max(POST_MAX),
  contentWarning: z.string().trim().max(120).nullable().optional(),
  sensitive: z.boolean().optional(),
  replyPolicy: z.enum(REPLY_POLICIES).optional(),
  location: z.string().trim().max(80).nullable().optional(),
});

export const createCommentSchema = z
  .object({
    content: z.string().trim().max(POST_MAX).default(""),
    parentId: z.string().uuid().optional(),
    mediaIds: z.array(z.string().uuid()).max(4).default([]),
    gif: gifAttachmentSchema.optional(),
  })
  .refine((v) => v.content.length > 0 || v.mediaIds.length > 0 || !!v.gif, { message: "Comment cannot be empty" });

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "At least 3 characters")
  .max(32, "At most 32 characters")
  .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, "Lowercase letters, numbers and dashes");

export const topicSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, "Invalid topic");

export const createCommunitySchema = z.object({
  name: z.string().trim().min(2, "At least 2 characters").max(50),
  slug: slugSchema,
  description: z.string().trim().max(500).default(""),
  visibility: z.enum(["public", "private"]).default("public"),
  iconMediaId: z.string().uuid().nullable().optional(),
  topics: z.array(topicSchema).max(5).default([]),
});

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex colour like #4cc38a");

/** Banner colours a community can use instead of (or behind) an uploaded image. */
export const BANNER_PRESETS = ["graphite", "rose", "red", "orange", "amber", "violet", "blue", "teal", "green", "slate"] as const;
export type BannerPreset = (typeof BANNER_PRESETS)[number];

export const updateCommunitySchema = z.object({
  name: z.string().trim().min(2).max(50).optional(),
  description: z.string().trim().max(500).optional(),
  visibility: z.enum(["public", "private"]).optional(),
  iconMediaId: z.string().uuid().nullable().optional(),
  bannerMediaId: z.string().uuid().nullable().optional(),
  topics: z.array(topicSchema).max(5).optional(),
  publishToFeed: z.boolean().optional(),
  showMembersPublicly: z.boolean().optional(),
  blockedWords: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(100).optional(),
  accentColor: hexColor.nullable().optional(),
  bannerColor: z.enum(BANNER_PRESETS).nullable().optional(),
  showBannerInSidebar: z.boolean().optional(),
  defaultChannelId: z.string().uuid().nullable().optional(),
  /** Welcome messages for new members are posted here; null turns them off. */
  joinAnnounceChannelId: z.string().uuid().nullable().optional(),
  rules: z.string().trim().max(2000).optional(),
  minAccountAgeDays: z.number().int().min(0).max(365).optional(),
  blockLinks: z.boolean().optional(),
  ownerId: z.string().uuid().optional(),
});

/** A permission bitfield: the full signed int32 range, since bit 31 (STREAM) is the sign bit. */
export const permissionBits = z.number().int().min(-2147483648).max(2147483647);

export const channelTypeSchema = z.enum(["text", "voice", "announcement", "forum", "media", "stage"]);
export type ChannelType = z.infer<typeof channelTypeSchema>;

/**
 * Channel names by type: text-like channels are lower-case-with-dashes
 * ("off-topic"); voice and stage channels keep their spelling ("Town Hall").
 * Returns "" when nothing usable is left.
 */
export function normaliseChannelName(name: string, type: ChannelType): string {
  const trimmed = name.trim().replace(/\s+/g, " ");
  if (type === "voice" || type === "stage") return trimmed.replace(/[\u0000-\u001f]/g, "").slice(0, 32).trim();
  return trimmed
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
    .slice(0, 32);
}
const channelName = z.string().trim().min(1, "Give the channel a name").max(100);

export const createChannelSchema = z.object({
  /** Normalised for the channel type by the server (normaliseChannelName). */
  name: channelName,
  type: channelTypeSchema.default("text"),
  categoryId: z.string().uuid().nullable().optional(),
  topic: z.string().trim().max(1024).default(""),
  /** Left out (with allowedRoleIds), a channel in a category copies the category's permissions. */
  isPrivate: z.boolean().optional(),
  allowedRoleIds: z.array(z.string().uuid()).max(50).optional(),
});
const roleOverwritesSchema = z.array(z.object({ roleId: z.string().uuid(), allow: permissionBits, deny: permissionBits })).max(50);
const memberOverwritesSchema = z.array(z.object({ userId: z.string().uuid(), allow: permissionBits, deny: permissionBits })).max(100);
export const updateChannelSchema = z.object({
  name: channelName.optional(),
  topic: z.string().trim().max(1024).optional(),
  categoryId: z.string().uuid().nullable().optional(),
  slowModeSeconds: z.number().int().min(0).max(21600).optional(),
  isPrivate: z.boolean().optional(),
  allowedRoleIds: z.array(z.string().uuid()).max(50).optional(),
  userLimit: z.number().int().min(0).max(99).optional(),
  /** Text and announcement channels can switch between the two. */
  type: z.enum(["text", "announcement"]).optional(),
  ageRestricted: z.boolean().optional(),
  overwrites: roleOverwritesSchema.optional(),
  /** Per-member overrides (e.g. a ticket channel opened for one person). Replaces the whole set. */
  memberOverwrites: memberOverwritesSchema.optional(),
  /** Copy the category's permissions (after any category change in the same request). Takes precedence over the fields above. */
  syncPermissions: z.boolean().optional(),
  /** Forum and media channels: tags members can put on posts. Replaces the whole set. */
  forumTags: z.lazy(() => forumTagsSchema).optional(),
});

/* ───────────── Forums ───────────── */

export const FORUM_TAG_MAX = 20;
export const forumTagSchema = z.object({
  id: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1, "Name the tag").max(20),
  emoji: z.string().trim().max(16).optional(),
});
export const forumTagsSchema = z.array(forumTagSchema).max(FORUM_TAG_MAX, `Up to ${FORUM_TAG_MAX} tags`);

export const THREAD_TITLE_MAX = 100;
export const createThreadSchema = z
  .object({
    title: z.string().trim().min(1, "Give your post a title").max(THREAD_TITLE_MAX),
    tagIds: z.array(z.string().min(1).max(40)).max(5).default([]),
    content: z.string().max(2000).default(""),
    mediaIds: z.array(z.string().uuid()).max(10).default([]),
    nonce: z.string().max(64).optional(),
  })
  .refine((v) => v.content.trim().length > 0 || v.mediaIds.length > 0, { message: "Write something or attach a file", path: ["content"] });
export const updateThreadSchema = z.object({
  title: z.string().trim().min(1).max(THREAD_TITLE_MAX).optional(),
  tagIds: z.array(z.string().min(1).max(40)).max(5).optional(),
  pinned: z.boolean().optional(),
  locked: z.boolean().optional(),
});
export const threadListQuery = z.object({
  sort: z.enum(["activity", "created"]).default("activity"),
  tag: z.string().min(1).max(40).optional(),
  cursor: z.string().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(25),
});

/* ───────────── Onboarding ───────────── */

/** A member finishing onboarding: rules accepted and answers (questionId → optionIds). */
export const onboardingSubmitSchema = z.object({
  acceptRules: z.boolean().default(false),
  answers: z.record(z.string().max(40), z.array(z.string().max(40)).max(20)).default({}),
});
export const reorderChannelsSchema = z.object({
  items: z
    .array(z.object({ id: z.string().uuid(), position: z.number().int().min(0), categoryId: z.string().uuid().nullable() }))
    .max(500),
});
export const reorderCategoriesSchema = z.object({
  items: z.array(z.object({ id: z.string().uuid(), position: z.number().int().min(0) })).max(100),
});

export const createCategorySchema = z.object({ name: z.string().trim().min(1).max(32) });
/** Name and the permission template; changing permissions also updates every channel that was synced with the category. */
export const updateCategorySchema = z.object({
  name: z.string().trim().min(1).max(32).optional(),
  isPrivate: z.boolean().optional(),
  allowedRoleIds: z.array(z.string().uuid()).max(50).optional(),
  overwrites: roleOverwritesSchema.optional(),
  memberOverwrites: memberOverwritesSchema.optional(),
});

export const roleSchema = z.object({
  name: z.string().trim().min(1).max(32),
  color: hexColor.nullable().default(null),
  permissions: permissionBits,
  hoist: z.boolean().default(false),
});
export const updateRoleSchema = z.object({
  name: z.string().trim().min(1).max(32).optional(),
  color: hexColor.nullable().optional(),
  permissions: permissionBits.optional(),
  hoist: z.boolean().optional(),
});
/** Time-out lengths offered in the UI, in minutes. 0 lifts an active time-out. */
export const TIMEOUT_OPTIONS = [1, 5, 10, 60, 60 * 24, 60 * 24 * 7] as const;
export const timeoutSchema = z.object({ minutes: z.number().int().min(0).max(60 * 24 * 28), reason: z.string().trim().max(200).default("") });
export const setMemberRolesSchema = z.object({ roleIds: z.array(z.string().uuid()).max(50) });

export const MESSAGE_MAX = 2000;

export const sendMessageSchema = z
  .object({
    content: z.string().max(MESSAGE_MAX).default(""),
    replyToId: z.string().uuid().optional(),
    mediaIds: z.array(z.string().uuid()).max(10).default([]),
    stickerIds: z.array(z.string().uuid()).max(3).default([]),
    gif: gifAttachmentSchema.optional(),
    /** Forum/media channel post to reply in. */
    threadId: z.string().uuid().optional(),
    /** Don't render link previews for this message. */
    suppressEmbeds: z.boolean().default(false),
    nonce: z.string().max(64).optional(),
    /** Channel messages only: attach a poll (the message may then have no text). */
    poll: createMessagePollSchema.optional(),
    /** Direct messages in encrypted conversations: the body, end-to-end encrypted (content must be empty). */
    encrypted: encryptedPayloadSchema.optional(),
  })
  .refine((v) => v.content.trim().length > 0 || v.mediaIds.length > 0 || v.stickerIds.length > 0 || !!v.gif || !!v.poll || !!v.encrypted, {
    message: "Message cannot be empty",
  });

export const forwardSchema = z.object({
  targets: z
    .array(z.object({ type: z.enum(["channel", "conversation"]), id: z.string().uuid() }))
    .min(1)
    .max(5),
  comment: z.string().max(MESSAGE_MAX).default(""),
});

export const editMessageSchema = z.object({ content: z.string().trim().min(1).max(MESSAGE_MAX) });

/** Unicode emoji, or a custom emoji written as `<:name:uuid>` / `<a:name:uuid>`. */
export const reactionSchema = z.object({ emoji: z.string().min(1).max(80) });
export const CUSTOM_EMOJI_RE = /<(a?):([a-zA-Z0-9_]{2,32}):([0-9a-f-]{36})>/g;

export const createConversationSchema = z.object({
  userIds: z.array(z.string().uuid()).min(1).max(9),
  name: z.string().trim().max(50).optional(),
});
export const updateConversationSchema = z.object({
  name: z.string().trim().max(50).nullable().optional(),
  addUserIds: z.array(z.string().uuid()).max(9).optional(),
});

export const inviteSchema = z.object({
  userId: z.string().uuid().optional(),
  channelId: z.string().uuid().optional(),
  maxUses: z.number().int().min(0).max(1000).default(0),
  expiresInHours: z.number().int().min(0).max(24 * 30).default(24 * 7),
});

export const banSchema = z.object({ reason: z.string().trim().max(200).default("") });

/* ───────────── Preferences (synced across devices) ───────────── */

export const ACCENTS = ["atrium", "blue", "violet", "green", "amber", "rose", "mono"] as const;
export type Accent = (typeof ACCENTS)[number];

/** App actions that can be bound to a key. */
export const KEYBIND_ACTIONS = [
  "goCommunities",
  "goFeed",
  "goExplore",
  "goMessages",
  "goNotifications",
  "goSettings",
  "newPost",
  "toggleMembers",
  "toggleMute",
  "toggleDeafen",
  "search",
  "quickSwitcher",
  "shortcutHelp",
  "commandPalette",
  "markAllRead",
] as const;
export type KeybindAction = (typeof KEYBIND_ACTIONS)[number];

export const DEFAULT_KEYBINDS: Record<KeybindAction, string> = {
  goCommunities: "[",
  goFeed: "]",
  goExplore: "E",
  goMessages: "M",
  goNotifications: "B",
  goSettings: "Ctrl+,",
  newPost: "N",
  toggleMembers: "Ctrl+U",
  toggleMute: "Ctrl+Shift+M",
  toggleDeafen: "Ctrl+Shift+D",
  search: "/",
  quickSwitcher: "Ctrl+K",
  shortcutHelp: "Ctrl+/",
  commandPalette: "Ctrl+Shift+P",
  markAllRead: "Shift+Escape",
};

const keybind = z.string().max(40);

export const FONT_FAMILIES = ["inter", "system", "humanist", "rounded", "serif", "mono"] as const;
export const CODE_FONTS = ["system", "jetbrains", "cascadia", "fira", "courier"] as const;
export const TYPOGRAPHY_PRESETS = ["default", "compact", "readable", "large", "custom"] as const;
export const LOCALES = ["en-GB", "en-US", "en-AU", "de-DE", "fr-FR", "es-ES", "it-IT", "nl-NL", "pt-BR", "pl-PL", "sv-SE", "ja-JP"] as const;
export const NOTIFICATION_SOUNDS = ["blip", "chime", "pop", "knock"] as const;
/** Rail entries a user can reorder or hide. */
export const NAV_ITEMS = ["communities", "feed", "explore", "messages", "notifications", "saved"] as const;
export type NavItem = (typeof NAV_ITEMS)[number];

const hexOrNull = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/)
  .nullable();

export const preferencesSchema = z.object({
  // Appearance
  theme: z.enum(["dark", "light", "system"]),
  accent: z.enum(ACCENTS),
  /** Overrides the accent preset with any colour. */
  customAccent: hexOrNull,
  /** Custom interface colours; null keeps the theme's value. */
  customColors: z.object({ background: hexOrNull, surface: hexOrNull, sidebar: hexOrNull, text: hexOrNull }),
  sidebarStyle: z.enum(["default", "contrast", "flush"]),
  messageStyle: z.enum(["flat", "bubbles"]),
  /**
   * Background tint over the page: "corner" glows one colour from a corner and
   * fades to the theme's base towards the opposite one; "gradient" runs between
   * two colours from that corner to the opposite.
   */
  backgroundEffect: z.enum(["off", "corner", "gradient"]),
  backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  backgroundColor2: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  backgroundCorner: z.enum(["top-left", "top-right", "bottom-left", "bottom-right"]),
  /** How strongly the tint shows, 0.1–1. */
  backgroundStrength: z.number().min(0.1).max(1),
  /** Corner radius multiplier. */
  radius: z.number().min(0).max(2),
  /** Spacing multiplier. */
  spacing: z.number().min(0.75).max(1.35),
  borders: z.enum(["subtle", "none", "strong"]),
  highContrast: z.boolean(),
  navOrder: z.array(z.enum(NAV_ITEMS)).max(NAV_ITEMS.length),
  navHidden: z.array(z.enum(NAV_ITEMS)).max(NAV_ITEMS.length),
  // Typography
  typographyPreset: z.enum(TYPOGRAPHY_PRESETS),
  fontFamily: z.enum(FONT_FAMILIES),
  fontWeight: z.number().int().min(300).max(600),
  headingWeight: z.number().int().min(400).max(800),
  usernameWeight: z.number().int().min(400).max(800),
  messageFontSize: z.number().min(12).max(22),
  lineHeight: z.number().min(1.15).max(1.9),
  letterSpacing: z.number().min(-0.03).max(0.08),
  codeFont: z.enum(CODE_FONTS),
  codeFontSize: z.number().min(11).max(20),
  // Motion & media
  reactionAnimations: z.boolean(),
  particleEffects: z.boolean(),
  animatedAvatars: z.boolean(),
  gifAutoplay: z.boolean(),
  /** How posts marked sensitive are shown. */
  sensitiveMedia: z.enum(["blur", "show", "hide"]),
  fontScale: z.number().min(0.85).max(1.25),
  density: z.enum(["comfortable", "compact"]),
  messageDisplay: z.enum(["cozy", "compact"]),
  roleColours: z.enum(["names", "off"]),
  reduceMotion: z.enum(["system", "on", "off"]),
  underlineLinks: z.boolean(),
  // Language & region
  clock: z.enum(["24h", "12h"]),
  dateFormat: z.enum(["dmy", "mdy", "ymd"]),
  locale: z.enum(LOCALES),
  timestamps: z.enum(["relative", "absolute"]),
  showSeconds: z.boolean(),
  // Feed
  defaultFeed: z.enum(["for_you", "following", "latest", "trending", "communities"]),
  showReposts: z.boolean(),
  showCounts: z.boolean(),
  autoplayMedia: z.boolean(),
  inlineComposer: z.boolean(),
  // Chat
  enterToSend: z.boolean(),
  sendTyping: z.boolean(),
  showTyping: z.boolean(),
  doubleClickToReply: z.boolean(),
  linkPreviews: z.boolean(),
  /** Show read receipts in direct messages (yours are shared per the privacy setting). */
  showReadReceipts: z.boolean(),
  // Notifications on this device
  desktopNotifications: z.boolean(),
  notificationSound: z.boolean(),
  notificationSoundChoice: z.enum(NOTIFICATION_SOUNDS),
  notificationVolume: z.number().min(0).max(1),
  pushNotifications: z.boolean(),
  quietHours: z.object({ enabled: z.boolean(), start: z.string().regex(/^\d{2}:\d{2}$/), end: z.string().regex(/^\d{2}:\d{2}$/) }),
  // Voice
  joinMuted: z.boolean(),
  noiseSuppression: z.boolean(),
  echoCancellation: z.boolean(),
  autoGainControl: z.boolean(),
  pushToTalk: z.boolean(),
  // Keyboard
  singleKeyShortcuts: z.boolean(),
  keybinds: z.object(Object.fromEntries(KEYBIND_ACTIONS.map((k) => [k, keybind])) as Record<KeybindAction, typeof keybind>),
  pushToTalkKey: keybind,
  // Trust & safety
  linkWarnings: z.boolean(),
  trustedDomains: z.array(z.string().trim().toLowerCase().min(1).max(253)).max(200),
  // Advanced
  developerMode: z.boolean(),
});
export type Preferences = z.infer<typeof preferencesSchema>;

export const DEFAULT_PREFERENCES: Preferences = {
  theme: "dark",
  accent: "atrium",
  customAccent: null,
  customColors: { background: null, surface: null, sidebar: null, text: null },
  sidebarStyle: "default",
  messageStyle: "flat",
  backgroundEffect: "off",
  backgroundColor: "#55c5bd",
  backgroundColor2: "#a58bff",
  backgroundCorner: "top-left",
  backgroundStrength: 0.5,
  radius: 1,
  spacing: 1,
  borders: "subtle",
  highContrast: false,
  navOrder: [...NAV_ITEMS],
  navHidden: [],
  typographyPreset: "default",
  fontFamily: "inter",
  fontWeight: 400,
  headingWeight: 600,
  usernameWeight: 600,
  messageFontSize: 15,
  lineHeight: 1.45,
  letterSpacing: 0,
  codeFont: "system",
  codeFontSize: 13,
  reactionAnimations: true,
  particleEffects: true,
  animatedAvatars: true,
  gifAutoplay: true,
  sensitiveMedia: "blur",
  fontScale: 1,
  density: "comfortable",
  messageDisplay: "cozy",
  roleColours: "names",
  reduceMotion: "system",
  underlineLinks: false,
  clock: "24h",
  dateFormat: "dmy",
  locale: "en-GB",
  timestamps: "relative",
  showSeconds: false,
  defaultFeed: "for_you",
  showReposts: true,
  showCounts: true,
  autoplayMedia: true,
  inlineComposer: true,
  enterToSend: true,
  sendTyping: true,
  showTyping: true,
  doubleClickToReply: false,
  linkPreviews: true,
  showReadReceipts: true,
  desktopNotifications: false,
  notificationSound: false,
  notificationSoundChoice: "blip",
  notificationVolume: 0.6,
  pushNotifications: false,
  quietHours: { enabled: false, start: "22:00", end: "08:00" },
  joinMuted: false,
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  pushToTalk: false,
  singleKeyShortcuts: true,
  keybinds: DEFAULT_KEYBINDS,
  pushToTalkKey: "`",
  linkWarnings: true,
  trustedDomains: [],
  developerMode: false,
};

export const updatePreferencesSchema = preferencesSchema
  .omit({ keybinds: true, quietHours: true, customColors: true })
  .partial()
  .extend({
    keybinds: preferencesSchema.shape.keybinds.partial().optional(),
    quietHours: preferencesSchema.shape.quietHours.partial().optional(),
    customColors: preferencesSchema.shape.customColors.partial().optional(),
  });
export type PreferencesPatch = z.infer<typeof updatePreferencesSchema>;

/** Merge stored (possibly partial or outdated) preferences over the defaults, dropping invalid keys. */
export function resolvePreferences(stored: unknown): Preferences {
  const raw = (stored && typeof stored === "object" ? stored : {}) as Record<string, unknown>;
  const merged: Record<string, unknown> = {
    ...DEFAULT_PREFERENCES,
    ...raw,
    keybinds: { ...DEFAULT_KEYBINDS, ...((raw.keybinds as object) ?? {}) },
    quietHours: { ...DEFAULT_PREFERENCES.quietHours, ...((raw.quietHours as object) ?? {}) },
    customColors: { ...DEFAULT_PREFERENCES.customColors, ...((raw.customColors as object) ?? {}) },
  };
  const out: Record<string, unknown> = {};
  const shape = preferencesSchema.shape as Record<string, z.ZodType>;
  for (const key of Object.keys(DEFAULT_PREFERENCES)) {
    const one = shape[key].safeParse(merged[key]);
    out[key] = one.success ? one.data : (DEFAULT_PREFERENCES as Record<string, unknown>)[key];
  }
  return out as Preferences;
}

/* ───────────── Connected accounts (shown on profiles) ───────────── */

export const CONNECTION_PROVIDERS = ["github", "gitlab", "bluesky", "mastodon", "spotify", "twitch", "youtube", "steam", "xbox", "playstation", "reddit", "website"] as const;
export type ConnectionProvider = (typeof CONNECTION_PROVIDERS)[number];

export const connectionSchema = z.object({
  provider: z.enum(CONNECTION_PROVIDERS),
  handle: z.string().trim().min(1).max(100),
  visible: z.boolean().default(true),
});
export type Connection = z.infer<typeof connectionSchema>;
export const setConnectionsSchema = z.object({ items: z.array(connectionSchema).max(CONNECTION_PROVIDERS.length) });

